/* eslint-disable prefer-arrow-callback */
import React from 'react';
import { filter, find } from 'lodash';
import PropTypes from 'prop-types';
import { useForm, FormProvider } from 'react-hook-form';
import { Box, Button, CardActions, CardContent, Grid, Paper, Typography } from '@availity/element';

import { AvailityHeaderLogo, Footer, MessageDisplay, SecurityQuestionFieldGroup } from '@/components';
import { useAppContext } from '@/context';
import { JOURNEY_SECURITY_QUESTIONS, handleSuccessNavigation } from '@/global';
import { useFRCallback } from '@/hooks';

const SecurityQuestionsSetup = ({ ...props }) => {
  const { stepToRender, setCurrentStep, journeyId } = props;
  const { setMessages, securityQuestionsOptions, queryParams = {} } = useAppContext();
  const { callbacks = [] } = useFRCallback(stepToRender);

  const { goto } = queryParams;

  const securityQuestionCallbacks = filter(
    callbacks,
    (callback) => callback.type?.toLowerCase() === 'kbacreatecallback'
  );

  // initialize form
  const formHandlers = useForm({
    defaultValues: {
      IDToken1question: '',
      IDToken1answer: '',
      IDToken2question: '',
      IDToken2answer: '',
      IDToken3question: '',
      IDToken3answer: '',
    },
  });
  const { handleSubmit } = formHandlers;

  const onSubmit = (values) => {
    setMessages([]);
    const output = stepToRender;

    if (values) {
      const { payload = {} } = output;
      const { callbacks = [] } = payload;

      const newCallbacks = callbacks;

      // iterate through original callbacks
      for (const [index, callbackObj] of callbacks.entries()) {
        // find the question and answer objects in the input for each callback
        const { input = [] } = callbackObj;
        const questionObj = find(input, (inputObj) => inputObj?.name?.indexOf('question') >= 0);
        const answerObj = find(input, (inputObj) => inputObj?.name?.indexOf('answer') >= 0);

        // get the field names
        const questionFieldId = questionObj?.name;
        const answerFieldId = answerObj?.name;

        // assign the field values from the form
        questionObj.value = values[questionFieldId];
        answerObj.value = values[answerFieldId]?.trim();

        // build the new input array with the form values
        const newInput = [{ ...questionObj }, { ...answerObj }];

        // override the original input with the new one
        newCallbacks[index].input = newInput;
      }

      // override the old callbacks with the new ones.
      Object.assign(output.payload.callbacks, [...newCallbacks]);

      setCurrentStep({ ...output });
    }
  };

  return securityQuestionsOptions ? (
    <Grid
      id="security-questions-setup-page-grid-container"
      container
      display="flex"
      flexDirection="row"
      flexWrap="wrap"
      spacing={0}
      sx={{ minHeight: '100vh', justifyContent: 'center' }}
    >
      <Grid
        id="2fa-start-page-content-grid"
        container
        display="flex"
        flexDirection="column"
        alignItems="center"
        flexGrow={1}
      >
        <AvailityHeaderLogo />
        <Grid direction="column" alignItems="center" justifyContent="center">
          <Paper variant="elevation" elevation={6} sx={{ width: { xs: '350px', sm: '500px', md: '700px' } }}>
            <FormProvider {...formHandlers}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <CardContent>
                  <Typography variant="h1" mb={2}>
                    Security Questions
                  </Typography>
                  <Typography variant="subtitle1" mb={2}>
                    Choose three unique security questions and answers.
                  </Typography>
                  {securityQuestionCallbacks.map((callback) => {
                    // map out callbacks to create each security question group
                    const { input = [] } = callback;

                    const questionObj = find(input, (inputObj) => inputObj?.name?.indexOf('question') >= 0);
                    const answerObj = find(input, (inputObj) => inputObj?.name?.indexOf('answer') >= 0);

                    return (
                      <Box key={`securityQuestions-${questionObj.name}`}>
                        <SecurityQuestionFieldGroup questionId={questionObj.name} answerId={answerObj.name} />
                      </Box>
                    );
                  })}
                  <MessageDisplay />
                </CardContent>
                <CardActions mt="auto" sx={{ justifyContent: 'end' }}>
                  {journeyId === JOURNEY_SECURITY_QUESTIONS && (
                    <Button mr={1} color="secondary" onClick={() => handleSuccessNavigation(goto)}>
                      Back
                    </Button>
                  )}
                  <Button ml={1} color="primary" type="submit">
                    Save & Continue
                  </Button>
                </CardActions>
              </form>
            </FormProvider>
          </Paper>
        </Grid>
        <Footer />
      </Grid>
    </Grid>
  ) : null;
};

SecurityQuestionsSetup.propTypes = {
  stepToRender: PropTypes.object.isRequired,
  setCurrentStep: PropTypes.func.isRequired,
  journeyId: PropTypes.string,
};

export default SecurityQuestionsSetup;
